import { forwardRef, type ComponentProps, type ReactElement, type ReactNode } from 'react';
import {
    Link as RouterLink,
    NavLink as RouterNavLink,
    parsePath,
    type LinkProps as RouterLinkProps,
    type NavLinkProps as RouterNavLinkProps,
} from 'react-router';
import { buildUrl, useSite } from '@salesforce/storefront-next-runtime/site-context';
import { useConfig } from '@salesforce/storefront-next-runtime/config';
import { useTranslation } from 'react-i18next';
import { useCurrentSiteAndLocaleRef } from '@/hooks/use-current-site-and-locale-ref';
import { ExternalLinkIcon } from './external-link-icon';
import { getNewTabLinkAttributes } from './new-tab-a11y';
import { isAllowedHref, isExternalHref, normalizeHref } from './utils';

export function useSiteAwareTo(to: RouterLinkProps['to']): RouterLinkProps['to'] {
    const { site } = useSite();
    const config = useConfig();
    const { siteRef, localeRef } = useCurrentSiteAndLocaleRef();

    // Query/hash-only destinations belong to the current route. In particular, the SDK
    // can mistake dots in a query (such as a price range) for a scheme-less domain.
    return typeof to === 'string' && parsePath(to).pathname && site
        ? buildUrl({
              to,
              urlConfig: config.url,
              params: { siteId: siteRef, localeId: localeRef },
          })
        : to;
}

/**
 * Site-context-aware <Link>. Drop-in replacement for React Router's <Link>.
 * Automatically prepends URL prefix and appends search params from Url config.
 * When no SiteProvider is mounted, behaves identically to React Router's Link.
 */
export const Link = forwardRef<HTMLAnchorElement, RouterLinkProps>(function Link({ to: _to, ...rest }, ref) {
    const to = useSiteAwareTo(_to);
    return <RouterLink ref={ref} to={to} {...rest} />;
});

/**
 * Site-context-aware <NavLink>. Drop-in replacement for React Router's <NavLink>.
 * Inherits all NavLink functionality (active class, aria-current).
 */
export const NavLink = forwardRef<HTMLAnchorElement, RouterNavLinkProps>(function NavLink({ to: _to, ...rest }, ref) {
    const to = useSiteAwareTo(_to);
    return <RouterNavLink ref={ref} to={to} {...rest} />;
});

export type LinkWrapperProps = Omit<ComponentProps<'a'>, 'href' | 'children'> & {
    to?: string;
    children: ReactNode;
    anotherTab?: boolean;
    /**
     * When `anotherTab` is true, append a decorative external icon if the caller
     * has not already provided a visual indicator. Opt-in to avoid breaking card layouts.
     */
    showExternalIcon?: boolean;
    /** Accessible name used to build the "opens in new tab" aria-label when `anotherTab` is true. */
    accessibleName?: string;
};

export function LinkWrapper({
    to,
    children,
    anotherTab = false,
    showExternalIcon = false,
    accessibleName,
    rel,
    'aria-label': ariaLabel,
    ...props
}: LinkWrapperProps): ReactElement | null {
    const { t } = useTranslation('common');
    const openInNewTabLabel = t('openInNewTab');
    const normalizedTo = normalizeHref(to);

    if (!isAllowedHref(normalizedTo)) {
        return null;
    }

    if (isExternalHref(normalizedTo)) {
        if (anotherTab) {
            const existingAriaLabel = typeof ariaLabel === 'string' ? ariaLabel : undefined;
            const nameForAria = accessibleName ?? existingAriaLabel;
            const newTabAttrs = getNewTabLinkAttributes(
                nameForAria
                    ? {
                          accessibleName: nameForAria,
                          openInNewTabLabel,
                          existingAriaLabel,
                      }
                    : undefined
            );

            return (
                <a
                    href={normalizedTo}
                    {...props}
                    target={newTabAttrs.target}
                    rel={newTabAttrs.rel}
                    aria-label={newTabAttrs['aria-label'] ?? existingAriaLabel}>
                    {children}
                    {!nameForAria ? <span className="sr-only"> ({openInNewTabLabel})</span> : null}
                    {showExternalIcon ? <ExternalLinkIcon className="ms-1 inline-block" /> : null}
                </a>
            );
        }

        return (
            <a href={normalizedTo} rel={rel} {...props} aria-label={ariaLabel}>
                {children}
            </a>
        );
    }
    return (
        <Link to={normalizedTo as string} {...props} aria-label={ariaLabel}>
            {children}
        </Link>
    );
}

export { ExternalLinkIcon } from './external-link-icon';
export { NewTabAnchor } from './new-tab-anchor';
export { buildOpenInNewTabAriaLabel, getNewTabLinkAttributes } from './new-tab-a11y';
export { useNewTabLinkProps } from './use-new-tab-link-props';
